import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import servicesData from "./servicesData";

const Container = ({ children }) => (
  <div className="max-w-7xl mx-auto px-6">{children}</div>
);

const ServicesPage = () => {
  const { serviceId } = useParams();
  // const navigate = useNavigate();
  const [activeSection, setActiveSection] = React.useState(serviceId || servicesData[0].id);

  // Scroll to the specific service section if serviceId is provided
  useEffect(() => {
    if (serviceId) {
      setTimeout(() => {
        const el = document.getElementById(serviceId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [serviceId]);

  // Scroll-spy: observe which section is in view
  useEffect(() => {
    const observers = [];
    servicesData.forEach((service) => {
      const el = document.getElementById(service.id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(service.id);
          }
        },
        { rootMargin: "-200px 0px -50% 0px", threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="font-sans">
      {/* Navbar is rendered globally in App.js */}

      {/* Hero Banner */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-900 via-black to-slate-800 text-white">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-300 hover:text-white transition-colors mb-6 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </Link>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
            <p className="text-slate-300 text-lg max-w-2xl">
              Comprehensive financial and business advisory solutions designed to
              drive growth, efficiency, and sustainable success for your organization.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Quick Navigation */}
      <section className="py-4 bg-white border-b sticky top-[72px] z-40 shadow-sm">
        <Container>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {servicesData.map((service) => (
              <button
                key={service.id}
                onClick={() => scrollToSection(service.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${activeSection === service.id
                  ? "bg-black text-white shadow-md"
                  : "bg-gray-100 text-gray-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
              >
                {service.title}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* Service Sections */}
      <section className="py-16">
        <Container>
          <div className="space-y-24">
            {servicesData.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
                className="scroll-mt-56"
              >
              <div className={`grid md:grid-cols-2 gap-12 items-start ${index % 2 !== 0 ? "md:[direction:rtl]" : ""}`}>
            
            {/* Left: Description */}
                <div className={`flex flex-col justify-center [direction:ltr] ${index % 2 !== 0 ? "md:order-2" : ""}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-900 flex items-center justify-center shadow-sm">
                      {service.icon}
                    </div>
                    <span className="text-sm font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full tracking-wide">
                      0{index + 1}
                    </span>
                  </div>

                  <h2 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                    {service.title}
                  </h2>

                  {service.description.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-500 mb-4 leading-relaxed text-base">
                      {para}
                    </p>
                  ))}
                </div>

                  {/* Offerings Card */}
                  <div className={`bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 border border-gray-100 min-h-[450px] flex flex-col justify-between ${index % 2 !== 0 ? "md:order-1" : ""}`}>
                    <h3 className="font-semibold text-gray-800 mb-5 text-lg">
                      What we offer
                    </h3>
                    <ul className="space-y-4">
                      {service.offerings.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-0.5">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <a href="/#consultation" className="block mt-8">
                      <button className="w-full bg-slate-900 text-white py-3 rounded-xl font-medium hover:bg-black transition-all duration-300 shadow-md hover:shadow-lg active:scale-[0.98]">
                        {service.cta} →
                      </button>
                    </a>
                  </div>
                </div>

                {/* Divider */}
                {index < servicesData.length - 1 && (
                  <div className="border-b border-gray-100 mt-24" />
                )}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white text-center">
          <div className="absolute inset-0 bg-gradient-to-r from-white/5 via-transparent to-transparent animate-pulse"></div>
          <Container>
            <h2 className="text-4xl font-bold mb-4">
              Ready to Take the Next Step?
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Partner with experienced advisors to unlock growth, improve financial clarity, and build long-term value.
            </p>
            <a href="/#consultation">
              <button
                className="px-8 py-4 bg-white text-black rounded-xl font-semibold"
              >
                Schedule a Consultation
              </button>
            </a>
          </Container>
        </section>
    </div>
  );  
};

export default ServicesPage;
